import {
  BellSolid,
  CircleCheckSolid,
  CircleExclamationSolid,
  TriangleExclamationSolid,
  XmarkSolid,
} from '@allocab/allocab-icons';
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { ToastConfig, ToastInterface } from './types';

const DEFAULT_TOAST_CONFIG: ToastConfig = {
  message: '',
  severity: 'info',
  timer: 5000,
};

const iconForSeverity: Record<ToastConfig['severity'], ReactNode> = {
  success: <CircleCheckSolid width={16} height={16} className="fill-current" />,
  info: <BellSolid width={16} height={16} className="fill-current" />,
  warning: <TriangleExclamationSolid width={16} height={16} className="fill-current" />,
  error: <CircleExclamationSolid width={16} height={16} className="fill-current" />,
};

const colorForSeverity: Record<ToastConfig['severity'], string> = {
  success: 'text-green-500 bg-green-100 rounded-lg dark:bg-green-800 dark:text-green-200',
  info: 'text-blue-500 bg-blue-100 rounded-lg dark:bg-blue-800 dark:text-blue-200',
  warning: 'text-orange-500 bg-orange-100 rounded-lg dark:bg-orange-800 dark:text-orange-200',
  error: 'text-red-500 bg-red-100 rounded-lg dark:bg-red-800 dark:text-red-200',
};

const Toast = forwardRef<ToastInterface>((_props, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>(DEFAULT_TOAST_CONFIG);

  const show: ToastInterface['show'] = config => {
    setToastConfig({
      message: config.message ?? DEFAULT_TOAST_CONFIG.message,
      severity: config.severity || DEFAULT_TOAST_CONFIG.severity,
      timer: config.timer ?? DEFAULT_TOAST_CONFIG.timer,
    });
    setIsVisible(true);
  };

  const hide: ToastInterface['hide'] = () => {
    setIsVisible(false);
  };

  const handleDismiss = () => {
    hide();
  };

  useImperativeHandle(ref, () => ({ show, hide }));

  useEffect(() => {
    if (isVisible && toastConfig?.timer != null && toastConfig.timer > 0) {
      const timer = setTimeout(hide, toastConfig.timer);

      return () => clearTimeout(timer);
    }
  }, [toastConfig?.timer, isVisible]);

  return (
    <div
      className={twJoin(
        'fixed top-4 m-auto left-0 md:left-64 right-0  z-[120] flex items-center w-full max-w-sm p-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-800',
        !isVisible && 'hidden',
      )}
      role="alert"
    >
      <div
        className={twJoin(
          'inline-flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg',
          colorForSeverity[toastConfig.severity],
        )}
      >
        {iconForSeverity[toastConfig.severity]}
      </div>
      <div className="ml-3 text-sm font-normal">{toastConfig.message}</div>
      <button
        type="button"
        className="ml-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700"
        data-dismiss-target="#toast-default"
        aria-label="Close"
        onClick={handleDismiss}
      >
        <span className="sr-only">Close</span>
        <XmarkSolid width={12} height={12} className="fill-current" />
      </button>
    </div>
  );
});

Toast.displayName = 'Toast';

export { Toast };
